import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { dashboardApi } from '../services/api.js';

function OutboundSummaryPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const extension = searchParams.get('extension');

  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState(searchParams.get('start') || getToday());
  const [endDate, setEndDate] = useState(searchParams.get('end') || getToday());
  const [dateError, setDateError] = useState('');

  function getToday() {
    return new Date().toISOString().split('T')[0];
  }

  function getMonthStart() {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
  }

  function getPrevMonthStart() {
    const d = new Date();
    // FIX: Use year/month arithmetic instead of setMonth which can be buggy near month boundaries
    let year = d.getFullYear();
    let month = d.getMonth() - 1; // 0-based month
    if (month < 0) {
      month = 11;
      year -= 1;
    }
    return `${year}-${String(month + 1).padStart(2, '0')}-01`;
  }

  function getPrevMonthEnd() {
    const d = new Date();
    // FIX: Day 0 of current month = last day of previous month
    const lastDay = new Date(d.getFullYear(), d.getMonth(), 0);
    return `${lastDay.getFullYear()}-${String(lastDay.getMonth() + 1).padStart(2, '0')}-${String(lastDay.getDate()).padStart(2, '0')}`;
  }

  function validateDateRange(start, end) {
    if (!start || !end) return true;
    const startObj = new Date(start);
    const endObj = new Date(end);
    const maxEnd = new Date(startObj);
    maxEnd.setMonth(maxEnd.getMonth() + 3);

    if (endObj > maxEnd) {
      setDateError('Date range limited to 3 months maximum');
      return false;
    }
    if (startObj > endObj) {
      setDateError('Start date cannot be after end date');
      return false;
    }
    setDateError('');
    return true;
  }

  const loadCalls = async (start, end) => {
    if (!extension) return;
    if (!validateDateRange(start, end)) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await dashboardApi.getOutboundDetail(extension, { start, end });
      setCalls(res.data.calls);
    } catch (err) {
      console.error('Failed to load outbound calls:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCalls(startDate, endDate);
  }, [extension, startDate, endDate]);

  const handleDateChange = (newStart, newEnd) => {
    setStartDate(newStart);
    setEndDate(newEnd);
    navigate(`/outbound-summary?extension=${extension}&start=${newStart}&end=${newEnd}`, { replace: true });
  };

  const setCurrentMonth = () => {
    handleDateChange(getMonthStart(), getToday());
  };

  const setPreviousMonth = () => {
    handleDateChange(getPrevMonthStart(), getPrevMonthEnd());
  };

  return (
    <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginBottom: '20px', flexWrap: 'wrap', gap: '12px'
      }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '700', color: 'var(--text-primary)' }}>
            Outbound Calls — Extension {extension}
          </h2>
          <div style={{ fontSize: '13px', color: 'var(--text-muted)', marginTop: '4px' }}>
            {calls.length} calls from {startDate} to {endDate}
          </div>
        </div>
        <button 
          onClick={() => navigate('/')}
          style={{
            padding: '8px 16px', background: 'var(--primary-light)', color: 'var(--primary)',
            border: '1px solid var(--primary)', borderRadius: 'var(--radius-sm)',
            cursor: 'pointer', fontSize: '13px', fontWeight: '500', fontFamily: 'inherit'
          }}
        >
          ← Back to Dashboard
        </button>
      </div>

      <div style={{
        background: 'var(--bg-card)', border: '1px solid var(--border)',
        borderRadius: 'var(--radius)', padding: '16px', marginBottom: '20px',
        display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center'
      }}>
        <button onClick={setCurrentMonth} style={{
          padding: '8px 14px', background: 'var(--primary)', color: 'white',
          border: 'none', borderRadius: 'var(--radius-sm)', cursor: 'pointer',
          fontSize: '13px', fontWeight: '500', fontFamily: 'inherit'
        }}>
          Current Month
        </button>
        <button onClick={setPreviousMonth} style={{
          padding: '8px 14px', background: 'var(--bg-body)', color: 'var(--text-primary)',
          border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)',
          cursor: 'pointer', fontSize: '13px', fontWeight: '500', fontFamily: 'inherit'
        }}>
          Previous Month
        </button>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: 'auto' }}>
          <label style={{ fontSize: '13px', color: 'var(--text-secondary)', fontWeight: '500' }}>From:</label>
          <input
            type="date"
            value={startDate}
            max={endDate}
            onChange={e => handleDateChange(e.target.value, endDate)}
            style={{
              padding: '6px 10px', border: '1px solid var(--border)',
              borderRadius: 'var(--radius-sm)', fontSize: '13px', fontFamily: 'inherit'
            }}
          />
          <label style={{ fontSize: '13px', color: 'var(--text-secondary)', fontWeight: '500' }}>To:</label>
          <input
            type="date"
            value={endDate}
            min={startDate}
            onChange={e => handleDateChange(startDate, e.target.value)}
            style={{
              padding: '6px 10px', border: '1px solid var(--border)',
              borderRadius: 'var(--radius-sm)', fontSize: '13px', fontFamily: 'inherit'
            }}
          />
        </div>
      </div>

      {dateError && (
        <div style={{
          background: 'var(--danger-light)', color: 'var(--danger)',
          padding: '10px 14px', borderRadius: 'var(--radius-sm)',
          fontSize: '13px', fontWeight: '500', marginBottom: '16px'
        }}>
          {dateError}
        </div>
      )}

      <div style={{
        background: 'var(--bg-card)', border: '1px solid var(--border)',
        borderRadius: 'var(--radius)', overflow: 'hidden'
      }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>
            Loading calls...
          </div>
        ) : calls.length > 0 ? (
          <div style={{ overflowX: 'auto', maxHeight: 'calc(100vh - 280px)', overflowY: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
              <thead style={{ position: 'sticky', top: 0, zIndex: 10 }}>
                <tr style={{ backgroundColor: 'var(--primary)', color: 'white' }}>
                  <th style={{ padding: '10px', textAlign: 'left', fontWeight: '600' }}>Call Date</th>
                  <th style={{ padding: '10px', textAlign: 'left', fontWeight: '600' }}>Destination</th>
                  <th style={{ padding: '10px', textAlign: 'right', fontWeight: '600' }}>Talk Time</th>
                </tr>
              </thead>
              <tbody>
                {calls.map((call, i) => (
                  <tr key={i} style={{
                    backgroundColor: i % 2 === 0 ? '#f9fafb' : 'white',
                    borderBottom: '1px solid var(--border-light)'
                  }}>
                    <td style={{ padding: '10px' }}>
                      {new Date(call.calldate).toLocaleString('en-ZA')}
                    </td>
                    <td style={{ padding: '10px', fontWeight: '500' }}>{call.dial_number || call.destination}</td>
                    <td style={{ padding: '10px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                      {call.talk_time}s
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>
            No outbound calls found for this period.
          </div>
        )}
      </div>
    </div>
  );
}

export default OutboundSummaryPage;

